// ./src/components/features/WelcomeIntro/WelcomeIntro.tsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { Stack, Title, Text, Paper, List, Code, Box } from "@mantine/core";
import { AppButton } from "@/components/generic";

export interface WelcomeIntroProps {
  /** Whether the component should be displayed */
  display?: boolean;
  /** Additional styling */
  style?: React.CSSProperties;
}

export const WelcomeIntro: React.FC<WelcomeIntroProps> = ({
  display = true,
  style,
}) => {
  const navigate = useNavigate();

  if (!display) {
    return null;
  }

  const handleCreateForm = () => {
    navigate("/forms/add");
  };

  return (
    <Stack gap="xl" style={style}>
      {/* What is FormSubmits */}
      <Paper p="xl" radius="md" withBorder>
        <Stack gap="md">
          <Title order={2} size="h3">
            What is FormSubmits?
          </Title>

          <Text size="md" lh={1.6}>
            FormSubmits is a form backend service that handles submissions from
            your HTML forms or AJAX requests. It validates submitted data on the
            server, stores submissions, and sends notifications.
          </Text>

          <Text size="md" lh={1.6}>
            You define validation rules when you create a form configuration,
            point your form on your website to our API endpoint, and we handle
            the rest.
          </Text>
        </Stack>
      </Paper>

      {/* Getting Started */}
      <Paper p="xl" radius="md" withBorder>
        <Stack gap="lg">
          <Title order={2} size="h3">
            Getting Started
          </Title>

          <List spacing="md" size="md" type="ordered">
            <List.Item>
              <Text fw={500}>Create a form</Text>
              <Text size="sm" c="dimmed">
                Go to the Forms page and create a new form. You'll get a unique
                form ID.
              </Text>
            </List.Item>
            <List.Item>
              <Text fw={500}>Add fields (optional)</Text>
              <Text size="sm" c="dimmed">
                Define field types and validation rules. Without fields,
                FormSubmits accepts any data.
              </Text>
            </List.Item>
            <List.Item>
              <Text fw={500}>Integrate with your website</Text>
              <Text size="sm" c="dimmed">
                Use the form's API endpoint as your HTML form action or fetch
                URL.
              </Text>
            </List.Item>
            <List.Item>
              <Text fw={500}>Receive submissions</Text>
              <Text size="sm" c="dimmed">
                View submissions in the dashboard and receive email
                notifications.
              </Text>
            </List.Item>
          </List>

          <AppButton
            variant="primary"
            iconName="Plus"
            onClick={handleCreateForm}
          >
            Create a Form
          </AppButton>
        </Stack>
      </Paper>

      {/* Integration Examples */}
      <Paper p="xl" radius="md" withBorder>
        <Stack gap="lg">
          <Title order={2} size="h3">
            Integration
          </Title>

          <Box>
            <Text size="md" fw={500} mb="xs">
              HTML Form
            </Text>
            <Text size="sm" c="dimmed" mb="sm">
              Set the form action to your FormSubmits endpoint. Users are
              redirected after submission.
            </Text>
            <Code block p="md" style={{ fontSize: "13px" }}>
              {`<form action="https://api.formsubmits.com/forms/{formId}/submit" method="POST">
  <input type="text" name="name" required />
  <input type="email" name="email" required />
  <textarea name="message"></textarea>
  <button type="submit">Send</button>
</form>`}
            </Code>
          </Box>

          <Box>
            <Text size="md" fw={500} mb="xs">
              AJAX / Fetch
            </Text>
            <Text size="sm" c="dimmed" mb="sm">
              Submit via JavaScript and handle the JSON response in your code.
            </Text>
            <Code block p="md" style={{ fontSize: "13px" }}>
              {`const response = await fetch(
  'https://api.formsubmits.com/forms/{formId}/submit',
  {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: 'John',
      email: 'john@example.com',
      message: 'Hello'
    })
  }
);

const result = await response.json();

if (result.success) {
  // Show success message
} else {
  // Handle validation errors in result.errors
}`}
            </Code>
          </Box>

          <Box>
            <Text size="md" fw={500} mb="xs">
              With File Uploads
            </Text>
            <Text size="sm" c="dimmed" mb="sm">
              Use FormData for file uploads. Enable file uploads in form
              settings first.
            </Text>
            <Code block p="md" style={{ fontSize: "13px" }}>
              {`const formData = new FormData();
formData.append('name', 'John');
formData.append('email', 'john@example.com');
formData.append('resume', fileInput.files[0]);

const response = await fetch(
  'https://api.formsubmits.com/forms/{formId}/submit',
  {
    method: 'POST',
    body: formData
  }
);`}
            </Code>
          </Box>
        </Stack>
      </Paper>

      {/* Response Format */}
      <Paper p="xl" radius="md" withBorder>
        <Stack gap="lg">
          <Title order={2} size="h3">
            Response Format
          </Title>

          <Box>
            <Text size="md" fw={500} mb="xs">
              Success (HTTP 200)
            </Text>
            <Code block p="md" style={{ fontSize: "13px" }}>
              {`{
  "success": true,
  "submissionId": "sub_abc123"
}`}
            </Code>
          </Box>

          <Box>
            <Text size="md" fw={500} mb="xs">
              Validation Error (HTTP 400)
            </Text>
            <Code block p="md" style={{ fontSize: "13px" }}>
              {`{
  "success": false,
  "errors": {
    "email": "Invalid email format",
    "age": "Must be at least 18"
  }
}`}
            </Code>
          </Box>
        </Stack>
      </Paper>

      {/* Call to action */}
      <Paper p="xl" radius="md" withBorder>
        <Stack gap="md" align="center">
          <Title order={3} size="h4" ta="center">
            Ready to create your first form?
          </Title>
          <AppButton
            variant="primary"
            size="lg"
            iconName="Plus"
            onClick={handleCreateForm}
          >
            Create a Form
          </AppButton>
        </Stack>
      </Paper>
    </Stack>
  );
};
